import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState, createContext, useContext, } from "react";
import { DataGrid, } from "@mui/x-data-grid";
import { Button } from "@inteacc/ui";
import { GenericFilter } from "./generic-filter";
/** Product-wide theme defaults; individual headerTheme props take precedence. */
const GridHeaderThemeContext = createContext({ style: "primary-fill" });
export const GridHeaderThemeProvider = GridHeaderThemeContext.Provider;
export const useGridHeaderTheme = () => useContext(GridHeaderThemeContext);
const displayValue = (value) => {
    if (typeof value === "string" || typeof value === "number")
        return value;
    if (typeof value === "boolean")
        return value ? "Yes" : "No";
    return "—";
};
const toFilterClauses = (model) => model.items
    .filter((item) => item.value !== undefined && item.value !== "")
    .map((item) => ({
    field: item.field,
    operator: item.operator === "equals"
        ? "eq"
        : item.operator === "startsWith"
            ? "startsWith"
            : "contains",
    value: String(item.value),
}));
const toSortClauses = (model) => model
    .filter((item) => item.sort === "asc" || item.sort === "desc")
    .map((item) => ({
    field: item.field,
    direction: item.sort === "desc" ? "desc" : "asc",
}));
const selectedModel = (ids) => ({
    type: "include",
    ids: new Set(ids),
});
export const StandardDataGrid = ({ ariaLabel, bulkActions = [], advancedFilter, columns, density = "standard", headerTheme, emptyMessage = "No records match this view.", error, exportCommand, extensionSlots, loading = false, onDensityChange, onQueryStateChange, permission, profile = "StandardList", queryState, refresh, rowActions = [], rowCount, rows, savedViews = [], selectedIds, onSelectionChange, }) => {
    const applicationHeaderTheme = useGridHeaderTheme();
    const resolvedHeaderTheme = { ...applicationHeaderTheme, ...headerTheme };
    const [internalSelection, setInternalSelection] = useState([]);
    const currentSelection = selectedIds ?? internalSelection;
    const pageSizeOptions = [...new Set([queryState.page.pageSize, 10, 25, 50, 100])]
        .filter((size) => size > 0)
        .sort((left, right) => left - right);
    const visibleColumns = columns.filter((column) => permission?.canViewColumn?.(column.field) ?? true);
    const selectedRows = rows.filter((row) => currentSelection.includes(row.id));
    const gridColumns = useMemo(() => {
        const mapped = visibleColumns.map((column) => {
            const definition = {
                field: column.field,
                headerName: column.header,
                minWidth: column.minWidth ?? 120,
                sortable: column.sortable ?? true,
                filterable: column.filterable ?? true,
                hideable: column.hideable ?? true,
                ...(column.width === undefined ? {} : { width: column.width }),
                ...(column.align === undefined
                    ? {}
                    : { align: column.align, headerAlign: column.align }),
                ...(column.value
                    ? {
                        valueGetter: (_value, row) => column.value?.(row),
                    }
                    : {}),
                ...(column.render
                    ? {
                        renderCell: (parameters) => column.render?.(parameters.value, parameters.row),
                    }
                    : {}),
            };
            return definition;
        });
        if (rowActions.length > 0) {
            mapped.push({
                field: "__actions",
                headerName: "Actions",
                minWidth: 180,
                sortable: false,
                filterable: false,
                hideable: false,
                renderCell: ({ row }) => (_jsx("div", { className: "iaf-grid__row-actions", children: rowActions
                        .filter((action) => permission?.canRunRowAction?.(action.id, row) ?? true)
                        .map((action) => (_jsx("button", { onClick: () => action.execute(row), type: "button", children: action.label }, action.id))) })),
            });
        }
        return mapped;
    }, [permission, rowActions, visibleColumns]);
    const updateSelection = (ids) => {
        if (selectedIds === undefined)
            setInternalSelection(ids);
        onSelectionChange?.(ids);
    };
    if (error) {
        return (_jsxs("div", { className: "iaf-grid__state iaf-grid__state--error", role: "alert", children: [_jsx("strong", { children: "Unable to load records" }), _jsx("p", { children: error }), refresh ? _jsx(Button, { onClick: refresh, children: "Retry" }) : null] }));
    }
    return (_jsxs("section", { "aria-label": ariaLabel, className: "iaf-grid", "data-header-style": resolvedHeaderTheme.style ?? "primary-fill", style: {
            ...(resolvedHeaderTheme.background ? { "--iaf-grid-header-background": resolvedHeaderTheme.background } : {}),
            ...(resolvedHeaderTheme.textColor ? { "--iaf-grid-header-text": resolvedHeaderTheme.textColor } : {}),
            ...(resolvedHeaderTheme.borderColor ? { "--iaf-grid-header-border": resolvedHeaderTheme.borderColor } : {}),
            ...(resolvedHeaderTheme.borderWidth !== undefined
                ? { "--iaf-grid-header-border-width": `${Math.max(0, Math.min(3, resolvedHeaderTheme.borderWidth))}px` } : {}),
        }, children: [_jsxs("div", { className: "iaf-grid__toolbar", children: [extensionSlots?.beforeToolbar, _jsxs("label", { children: [_jsx("span", { children: "Search" }), _jsx("input", { "aria-label": "Search records", onChange: (event) => onQueryStateChange({
                                    ...queryState,
                                    page: { ...queryState.page, page: 0 },
                                    search: event.target.value,
                                }), type: "search", value: queryState.search })] }), savedViews.length > 0 ? (_jsxs("label", { children: [_jsx("span", { children: "Saved view" }), _jsxs("select", { "aria-label": "Saved view", onChange: (event) => {
                                    const view = savedViews.find((candidate) => candidate.id === event.target.value);
                                    if (view)
                                        onQueryStateChange(view.query);
                                }, value: queryState.savedViewId ?? "", children: [_jsx("option", { value: "", children: "Default view" }), savedViews.map((view) => (_jsx("option", { value: view.id, children: view.label }, view.id)))] })] })) : null, _jsxs("label", { children: [_jsx("span", { children: "Density" }), _jsxs("select", { "aria-label": "List density", onChange: (event) => onDensityChange?.(event.target.value), value: density, children: [_jsx("option", { value: "compact", children: "Compact" }), _jsx("option", { value: "standard", children: "Standard" }), _jsx("option", { value: "comfortable", children: "Comfortable" })] })] }), refresh ? (_jsx(Button, { onClick: refresh, tone: "neutral", children: "Refresh" })) : null, exportCommand ? (_jsx(Button, { onClick: () => exportCommand.execute(queryState), tone: "neutral", children: exportCommand.label ?? "Export" })) : null, extensionSlots?.afterToolbar] }), advancedFilter ? (_jsx(GenericFilter, { ...advancedFilter, applied: queryState.filterExpression ?? {
                    schemaVersion: 1,
                    root: { type: "group", id: "grid-empty-root", join: "and", children: [] },
                }, onApply: (expression) => onQueryStateChange({
                    ...queryState,
                    page: { ...queryState.page, page: 0 },
                    filterExpression: expression,
                }) })) : null, currentSelection.length > 0 && bulkActions.length > 0 ? (_jsxs("div", { className: "iaf-grid__bulk", role: "toolbar", "aria-label": "Bulk actions", children: [_jsxs("span", { children: [currentSelection.length, " selected"] }), bulkActions
                        .filter((action) => permission?.canRunBulkAction?.(action.id, selectedRows) ?? true)
                        .map((action) => (_jsx(Button, { onClick: () => action.execute(selectedRows), tone: "neutral", children: action.label }, action.id)))] })) : null, !loading && rows.length === 0 ? (_jsx("div", { className: "iaf-grid__state", children: extensionSlots?.empty ?? _jsx("p", { children: emptyMessage }) })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "iaf-grid__desktop", children: _jsx(DataGrid, { "aria-label": ariaLabel, checkboxSelection: profile !== "ReadOnlyInquiry", columns: gridColumns, density: density, disableRowSelectionOnClick: true, filterMode: "server", getRowId: (row) => row.id, isRowSelectable: ({ row }) => permission?.canSelectRow?.(row) ?? true, keepNonExistentRowsSelected: true, loading: loading, onFilterModelChange: (model) => onQueryStateChange({
                                ...queryState,
                                page: { ...queryState.page, page: 0 },
                                filters: toFilterClauses(model),
                            }), onPaginationModelChange: (model) => onQueryStateChange({
                                ...queryState,
                                page: { page: model.page, pageSize: model.pageSize },
                            }), onRowSelectionModelChange: (model) => updateSelection([...model.ids]), onSortModelChange: (model) => onQueryStateChange({
                                ...queryState,
                                sort: toSortClauses(model),
                            }), pageSizeOptions: pageSizeOptions, paginationMode: "server", paginationModel: queryState.page, rowCount: rowCount, rows: [...rows], rowSelectionModel: selectedModel(currentSelection), sortingMode: "server" }) }), _jsxs("div", { className: "iaf-grid__mobile", children: [_jsxs("p", { className: "iaf-grid__mobile-count", children: [rowCount, " records \u00B7 mobile summary"] }), _jsx("ul", { children: rows.map((row) => (_jsxs("li", { children: [_jsx("dl", { children: visibleColumns.slice(0, 4).map((column) => (_jsxs("div", { children: [_jsx("dt", { children: column.header }), _jsx("dd", { children: displayValue(column.value?.(row) ??
                                                            row[column.field] ??
                                                            "—") })] }, column.field))) }), _jsx("div", { className: "iaf-grid__row-actions", children: rowActions
                                                .filter((action) => permission?.canRunRowAction?.(action.id, row) ?? true)
                                                .map((action) => (_jsx("button", { onClick: () => action.execute(row), type: "button", children: action.label }, action.id))) })] }, row.id))) })] })] }))] }));
};
export { GenericFilter } from "./generic-filter";
//# sourceMappingURL=index.js.map